import React, {
  forwardRef,
  useEffect,
  useState,
  useImperativeHandle,
  useRef,
} from "react";
import styles from "../../styles/chat.module.scss";
import { useTheme, useUserInfo } from "../../context/ThemeContext";
import { Button, message, Space, Spin, Tooltip, UploadFile } from "antd";
import {
  ArrowUpOutlined,
  BorderOutlined,
  BulbOutlined,
  CloseCircleFilled,
  CloseOutlined,
  CodepenOutlined,
  FileZipOutlined,
  MergeFilled,
  PaperClipOutlined,
} from "@ant-design/icons";
import UpLoad, { accept } from "../UpLoad/UpLoad";
import IntroduceGitUrlModal from "../Modal/IntroduceGitUrlModal";
import { useCommonData } from "../../layout/ELayout";
import debounce from "lodash/debounce";
import { getGitBranch } from "../api/chat";
import { Button as MobileButton } from "antd-mobile";

// eslint-disable-next-line react/display-name
const ChatTextArea = forwardRef(
  (
    props: {
      handleInputChange: (e: { target: any }) => void;
      send: () => void;
      cancleFn: () => void;
      checkLogin: () => boolean;
      stop: boolean;
      quoteData?: any;
      clearQuote: () => void;
      handleUploadChange: (file: {
        openai_file_id: string;
        original_filename: string;
      }) => void;
    },
    ref: any
  ) => {
    const {
      stop,
      handleInputChange,
      send,
      cancleFn,
      checkLogin,
      quoteData,
      clearQuote,
      handleUploadChange,
    } = props;
    const { theme } = useTheme();
    const { commonData, changeCommonData } = useCommonData();
    const { userInfo } = useUserInfo();
    const [value, setValue] = useState("");
    const [upLoadfile, setUploadFile] = useState<UploadFile[]>([]);
    const [gitSuccess, setGitSuccess] = useState(true);
    const [repoUrl, setRepoUrl] = useState<{
      url: string;
      type: string;
      repo_full_name?: string;
      branch_name?: string;
    }>({
      url: "",
      type: "",
      repo_full_name: "",
      branch_name: "",
    });
    const [style, setStyle] = useState<React.CSSProperties>({
      width:
        document.body.offsetWidth - 280 > 1200 ? 1152 : "calc(100% - 48px)",
    });
    const sendRef = useRef<any>();

    const checkRepoLinksRef = useRef<(text: string) => void>(
      debounce((text: string) => {
        // 检查输入内容是否包含 github 或 gitee 链接
        // ...existing code...
        const githubRegex =
          /(https?:\/\/)?(www\.)?github\.com\/[A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+(\/tree\/[A-Za-z0-9_.\-\/%]+)?/gi;
        const giteeRegex =
          /(https?:\/\/)?(www\.)?gitee\.com\/[A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+(\/tree\/[A-Za-z0-9_.\-\/%]+)?/gi;
        const githubLinks = text.match(githubRegex) || [];
        const giteeLinks = text.match(giteeRegex) || [];
        if (githubLinks.length > 0 || giteeLinks.length > 0) {
          const url = [...githubLinks, ...giteeLinks][0];
          handleAddUrl(url);
        }
      }, 100)
    );

    useEffect(() => {
      if (upLoadfile.length === 0) {
        handleUploadChange({ openai_file_id: "", original_filename: "" });
      }
    }, [handleUploadChange, upLoadfile]);

    useEffect(() => {
      const textarea = document.querySelector("textarea");
      if (!value && textarea) {
        textarea.style.height = "24px"; // 恢复到初始高度
      }
    }, [value]);

    const onChange = (e: any) => {
      const textarea = e.target;
      setValue(textarea.value);
      textarea.style.height = "auto"; // 先重置高度
      textarea.style.height = `${textarea.scrollHeight}px`; // 根据内容调整高度
      if (!textarea.value) {
        textarea.style.height = "24px"; // 恢复到初始高度
      }
      handleInputChange(e);
    };
    // 使用 useImperativeHandle 来定义 ref 可以访问的方法
    useImperativeHandle(ref, () => ({
      setTextAreaValue: (test: string) => {
        setValue(test);
        checkRepoLinksRef.current?.(test);
        setTimeout(() => {
          const textarea = document.querySelector("textarea");
          if (textarea) {
            textarea.style.height = "auto";
            textarea.style.height = `${textarea.scrollHeight}px`;
          }
        }, 10);
      },
      setChatWidth: (style: React.CSSProperties) => {
        setStyle(style);
      },
      getRepoUrl: () => {
        return repoUrl;
      },
      initRepoData: (data: {
        url: string;
        type: string;
        repo_full_name?: string;
        branch_name?: string;
      }) => {
        setRepoUrl(data);
      },
      initFileData: (data: {
        openai_file_id: string;
        original_filename: string;
      }) => {
        setUploadFile([
          {
            uid: data.openai_file_id,
            name: data.original_filename,
            status: "done",
          },
        ]);
        handleUploadChange(data);
      },
      clear: () => {
        setRepoUrl({
          url: "",
          type: "",
          repo_full_name: "",
          branch_name: "",
        });
        setUploadFile([]);
        setGitSuccess(true);
        setValue("");
      },
      uploadFile,
    }));
    const uploadFile = (file: UploadFile) => {
      setUploadFile([file]);
      setRepoUrl({
        url: "",
        type: "",
        repo_full_name: "",
        branch_name: "",
      });
      setGitSuccess(false);
      if (file.status === "done") {
        if (file.response.code === 200) {
          message.success(`${file.name} 上传成功`);
          handleUploadChange(file.response.data);
          setGitSuccess(true);
        } else {
          message.error(`${file.response?.message}`);
          setGitSuccess(true);
          setUploadFile([]);
        }
      } else if (file.status === "error") {
        message.error(`${file.response?.message}`);
        setGitSuccess(true);
        setUploadFile([]);
      }
    };
    const handleAddUrl = (url: string) => {
      if (!checkLogin()) {
        return;
      }
      let type = "";
      if (url.includes("github.com")) {
        type = "github";
      } else if (url.includes("gitee.com")) {
        type = "gitee";
      }
      setRepoUrl({ ...repoUrl, url, type });
      if (!repoUrl.branch_name) {
        setGitSuccess(false);
        getGitBranch({ url, type }).then((res) => {
          if (res.code === 200) {
            setRepoUrl(res.data);
            setGitSuccess(true);
          } else {
            message.error(res.message.toString());
            setGitSuccess(false);
          }
        });
      }
      setUploadFile([]);
    };
    return (
      <div className={styles.chatpdfArea} style={style}>
        <div className={styles.input}>
          {!!quoteData?.startLineNumber && (
            <div className={styles.quote}>
              <div className={styles.quoteItem}>
                <div className={styles.quoteItemTitle}>
                  <span>
                    <span className={styles.text}>{quoteData?.filename}</span>
                    {quoteData?.startLineNumber && quoteData?.endLineNumber && (
                      <span className={styles.text}>
                        ( {quoteData?.startLineNumber.toString()}-
                        {quoteData?.endLineNumber.toString()})
                      </span>
                    )}
                  </span>
                  <Tooltip placement="top" title={"不再围绕这个文件问答"}>
                    <Button
                      type="text"
                      icon={<CloseOutlined style={{ fontSize: 12 }} />}
                      onClick={clearQuote}
                      size="small"
                    />
                  </Tooltip>
                </div>
              </div>
            </div>
          )}
          <div className={styles.textareaTools}>
            {repoUrl.repo_full_name || repoUrl.url ? (
              <div className={styles.repoUrl}>
                <MergeFilled style={{ fontSize: 20 }} />
                <div className={styles.name} title={repoUrl.url}>
                  <div className={styles.url}>{repoUrl.url}</div>
                  <div className={styles.branch_name}>
                    {repoUrl.branch_name}
                  </div>
                </div>
                <CloseCircleFilled
                  className={styles.closeIcon}
                  onClick={() => {
                    setGitSuccess(true);
                    setRepoUrl({
                      url: "",
                      type: "",
                      repo_full_name: "",
                      branch_name: "",
                    });
                  }}
                />
              </div>
            ) : (
              <div>
                {upLoadfile.map((file) => (
                  <Spin
                    key={file.uid}
                    spinning={file.status === "uploading"}
                    percent={file.percent}
                    className={styles.spinBox}
                  >
                    <div className={styles.uploadFile}>
                      <FileZipOutlined style={{ fontSize: 22 }} />
                      <div className={styles.name} title={file.name}>
                        {file.name}
                      </div>
                      <CloseCircleFilled
                        className={styles.closeIcon}
                        onClick={() => {
                          setUploadFile([]);
                        }}
                      />
                    </div>
                  </Spin>
                ))}
              </div>
            )}
          </div>
          <div className={styles.chatTextAreaBox}>
            <div className={styles.textareaBox}>
              <textarea
                wrap="off"
                style={{
                  color: theme === "dark" ? "#FFFFFF" : "#000000",
                }}
                rows={1}
                className={styles.textarea}
                placeholder="请在此输入您需要解读的内容"
                value={value}
                onChange={onChange}
                onPaste={(e) => {
                  // 这里可以获取粘贴的内容
                  const pasteText = e.clipboardData.getData("text");
                  // 例如：自动检测粘贴内容中的仓库链接
                  if (!(repoUrl.branch_name && gitSuccess)) {
                    checkRepoLinksRef.current?.(pasteText);
                  }
                }}
                onKeyDown={(e) => {
                  if (stop) {
                    return;
                  }
                  if (
                    e.code === "Enter" &&
                    !e.shiftKey &&
                    !e.nativeEvent.isComposing
                  ) {
                    e.preventDefault();
                    sendRef.current?.click();
                  }
                }}
              ></textarea>
            </div>
          </div>
          <div className={styles.textareaBtns}>
            <Space>
              <UpLoad
                disabled={stop}
                onUpLoadChange={(file) => {
                  if (!checkLogin()) {
                    return;
                  }
                  uploadFile(file);
                }}
              >
                <Button
                  type="default"
                  shape="circle"
                  icon={<PaperClipOutlined />}
                />
              </UpLoad>

              <Tooltip
                placement="top"
                title={
                  upLoadfile.length > 0
                    ? "请清空当前上传内容后再引入开源仓库"
                    : "引入开源仓库"
                }
              >
                <div>
                  <IntroduceGitUrlModal handleAddUrl={handleAddUrl}>
                    <Button
                      type="default"
                      shape="circle"
                      icon={<CodepenOutlined />}
                      disabled={upLoadfile.length > 0}
                    />
                  </IntroduceGitUrlModal>
                </div>
              </Tooltip>
              <MobileButton
                block
                size="small"
                shape="rounded"
                fill="outline"
                color={commonData.deepThinking ? "primary" : "default"}
                onClick={() => {
                  changeCommonData({ deepThinking: !commonData.deepThinking });
                  localStorage.setItem(
                    "deepThinking",
                    localStorage.getItem("deepThinking") === "true"
                      ? "false"
                      : "true"
                  );
                }}
              >
                深度思考
              </MobileButton>
            </Space>
            <Space>
              {stop ? (
                <Button
                  type="default"
                  shape="circle"
                  onClick={cancleFn}
                  icon={<BorderOutlined />}
                />
              ) : (
                <Button
                  type="primary"
                  shape="circle"
                  ref={sendRef}
                  disabled={!(gitSuccess && !!value)}
                  onClick={send}
                  icon={<ArrowUpOutlined />}
                />
              )}
            </Space>
          </div>
        </div>
      </div>
    );
  }
);
export default ChatTextArea;
